<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { Connection, CopyDocument, Delete, Key, Link, MessageBox, Switch } from "@element-plus/icons-vue";
import { api, jsonBody } from "../api";

interface ApiKeyItem {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

interface DomainSettingsData {
  zone: string;
  mailboxDomain: string;
  webmailHost: string;
  webmailUrl: string;
  apiEndpoint: string;
  receivingEnabled: boolean;
  catchAllEnabled: boolean;
  catchAllTarget: string | null;
  contactAddress: string;
  apiKeys: ApiKeyItem[];
}

const settings = ref<DomainSettingsData | null>(null);
const loading = ref(true);
const saving = ref(false);
const creating = ref(false);
const tokenName = ref("邮件读取自动化");
const expiresInDays = ref<"30" | "90" | "365" | "never">("90");
const revealedToken = ref("");
const curlExample = computed(() => settings.value ? [
  `curl -X POST '${settings.value.apiEndpoint}' \\`,
  "  -H 'Authorization: <API_TOKEN>' \\",
  "  -H 'Content-Type: application/json' \\",
  `  -d '{"toEmail":"business@${settings.value.mailboxDomain}","timeSort":"desc","num":1,"size":20}'`,
].join("\n") : "");

async function load() {
  loading.value = true;
  try { settings.value = await api<DomainSettingsData>("/api/v1/admin/domain-settings"); }
  catch (error) { ElMessage.error(error instanceof Error ? error.message : "加载域名设置失败"); }
  finally { loading.value = false; }
}

async function updateSettings(receivingEnabled: boolean, catchAllEnabled: boolean) {
  if (!settings.value || saving.value) return;
  saving.value = true;
  try {
    await api("/api/v1/admin/domain-settings", { method: "PATCH", ...jsonBody({ receivingEnabled, catchAllEnabled }) });
    settings.value.receivingEnabled = receivingEnabled;
    settings.value.catchAllEnabled = catchAllEnabled;
    ElMessage.success("接收策略已生效");
  } catch (error) { ElMessage.error(error instanceof Error ? error.message : "保存失败"); }
  finally { saving.value = false; }
}

async function createToken() {
  if (!tokenName.value.trim()) return;
  creating.value = true;
  try {
    const created = await api<{ token: string }>("/api/v1/admin/api-keys", {
      method: "POST",
      ...jsonBody({ name: tokenName.value, expiresInDays: expiresInDays.value === "never" ? null : Number(expiresInDays.value) }),
    });
    await load();
    revealedToken.value = created.token;
    ElMessage.success("API Token 已创建；请立即复制");
  } catch (error) { ElMessage.error(error instanceof Error ? error.message : "创建 Token 失败"); }
  finally { creating.value = false; }
}

async function revokeToken(item: ApiKeyItem) {
  await ElMessageBox.confirm(`撤销 ${item.name}？使用该 Token 的程序会立即失效。`, "撤销 API Token", { type: "warning" });
  await api(`/api/v1/admin/api-keys/${item.id}`, { method: "DELETE" });
  ElMessage.success("API Token 已撤销");
  await load();
}

async function copy(value: string) {
  await navigator.clipboard.writeText(value);
  ElMessage.success("已复制到剪贴板");
}

onMounted(() => { void load(); });
</script>

<template>
  <section class="domain-settings-page" v-loading="loading">
    <div v-if="settings" class="domain-settings-shell">
      <header class="settings-hero">
        <div>
          <span class="eyebrow">DOMAIN MAIL ENGINE</span>
          <h1>{{ settings.mailboxDomain }}</h1>
          <p>源 Cloud Mail 接收语义已按域名隔离：接收总开关、无人收件与公开只读 API。</p>
        </div>
        <a class="quiet-button" :href="settings.webmailUrl" target="_blank" rel="noreferrer"><Link /> 打开此邮箱系统</a>
      </header>

      <div class="settings-grid">
        <article class="settings-card receive-policy-card">
          <div class="settings-card-heading"><div><span class="eyebrow">DELIVERY POLICY</span><h2>邮件接收</h2></div><MessageBox /></div>
          <div class="policy-row">
            <div><strong>接收邮件</strong><small>控制 {{ settings.mailboxDomain }} 的全部入站邮件。</small></div>
            <button class="setting-switch" :class="{ enabled: settings.receivingEnabled }" :aria-pressed="settings.receivingEnabled" :disabled="saving" @click="updateSettings(!settings.receivingEnabled, settings.catchAllEnabled)"><span /></button>
          </div>
          <div class="policy-row">
            <div><strong>无人收件</strong><small>未创建地址也接收，并归入 {{ settings.catchAllTarget || '域名管理员' }}。</small></div>
            <button class="setting-switch" :class="{ enabled: settings.catchAllEnabled }" :aria-pressed="settings.catchAllEnabled" :disabled="saving" @click="updateSettings(settings.receivingEnabled, !settings.catchAllEnabled)"><span /></button>
          </div>
          <div class="policy-route"><Switch /><span>任意地址</span><i /><span>{{ settings.catchAllTarget }}</span></div>
        </article>

        <article class="settings-card access-card">
          <div class="settings-card-heading"><div><span class="eyebrow">TENANT ENTRY</span><h2>独立访问入口</h2></div><Connection /></div>
          <dl class="access-ledger">
            <div><dt>Webmail</dt><dd><a :href="settings.webmailUrl" target="_blank" rel="noreferrer">{{ settings.webmailUrl }}</a></dd></div>
            <div><dt>官方联系</dt><dd><strong>{{ settings.contactAddress }}</strong></dd></div>
            <div><dt>邮箱后缀</dt><dd>@{{ settings.mailboxDomain }}</dd></div>
            <div><dt>兼容 API</dt><dd>{{ settings.apiEndpoint }}</dd></div>
          </dl>
          <p><code>{{ settings.contactAddress }}</code> 固定归入域名管理员；邮件保留在此 Webmail，并可由中央控制台即时通知企业微信群。</p>
        </article>
      </div>

      <article class="settings-card token-workbench">
        <div class="settings-card-heading"><div><span class="eyebrow">PROGRAMMATIC ACCESS</span><h2>邮件查看 API Token</h2></div><Key /></div>
        <div class="token-create-row">
          <label><span>Token 名称</span><input v-model="tokenName" maxlength="80" /></label>
          <label><span>有效期</span><select v-model="expiresInDays"><option value="30">30 天</option><option value="90">90 天</option><option value="365">1 年</option><option value="never">永不过期</option></select></label>
          <button class="primary-button" :disabled="creating || !tokenName.trim()" @click="createToken"><Key /> 创建只读 Token</button>
        </div>
        <div v-if="revealedToken" class="token-reveal">
          <div><span>仅显示这一次</span><code>{{ revealedToken }}</code></div>
          <button class="quiet-button" @click="copy(revealedToken)"><CopyDocument /> 复制</button>
        </div>
        <div class="token-list">
          <div v-for="item in settings.apiKeys" :key="item.id" class="token-row">
            <span class="token-icon"><Key /></span>
            <div><strong>{{ item.name }}</strong><code>{{ item.tokenPrefix }}••••••••</code></div>
            <span>{{ item.lastUsedAt ? `上次使用 ${new Date(item.lastUsedAt).toLocaleString()}` : '尚未使用' }}</span>
            <span>{{ item.expiresAt ? `${new Date(item.expiresAt).toLocaleDateString()} 到期` : '永不过期' }}</span>
            <button class="icon-button" title="撤销 Token" @click="revokeToken(item)"><Delete /></button>
          </div>
          <div v-if="!settings.apiKeys.length" class="empty-token">尚未创建 API Token</div>
        </div>
        <div class="api-example"><div><span>Cloud Mail 兼容调用</span><button class="text-button" @click="copy(curlExample)"><CopyDocument /> 复制示例</button></div><pre>{{ curlExample }}</pre></div>
      </article>
    </div>
  </section>
</template>
